import { CampaignTypeSchema } from "../shared/campaign-types";
import { z } from "zod";

// Zod schemas for runtime validation
const CampaignDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  icon: z.string().nullable(),
  title: z.string(),
});

const CalldataSchema = z.object({
  user: z.string(),
  cedeSafe: z.string(),
  epochId: z.number(),
  amount: z.string(),
  proof: z.array(z.string()),
});

export const ClaimablesDataSchema = z.object({
  campaign: z.string(),
  campaignType: CampaignTypeSchema,
  campaignData: CampaignDataSchema.optional(),
  rewardTokenSymbol: z.string(),
  tokenDecimals: z.number(),
  activeEpochId: z.number(),
  epochStart: z.coerce.date(),
  epochEnd: z.coerce.date(),
  calldata: CalldataSchema,
  // TODO multichain: make required once get-claimable-rewards populates it
  chainId: z.number().optional(),
  rewardTokenAddress: z.string().optional(),
});

export const ClaimBatchSchema = z.object({
  batchIndex: z.number(),
  batchCalldata: z.string(),
  claimCount: z.number(),
  // TODO multichain: make required once get-claimable-rewards populates it
  chainId: z.number().optional(),
});

export const ClaimableRewardsSchema = z.object({
  claimablesData: z.array(ClaimablesDataSchema),
  allClaimsEncoded: z.string(),
  batches: z.array(ClaimBatchSchema),
});

export const ClaimableSoonEpochRewardsSchema = z.object({
  campaign: z.string(),
  campaignType: CampaignTypeSchema,
  campaignData: CampaignDataSchema.optional(),
  rewardTokenSymbol: z.string(),
  amount: z.number(),
  epochId: z.number(),
  activeEpochId: z.number(),
  epochStart: z.coerce.date(),
  epochEnd: z.coerce.date(),
  cooldownEnd: z.string(),
});

// Schemas for API response wrappers
export const ClaimableRewardsResponseSchema = z.object({
  claimableEpochs: ClaimableRewardsSchema,
});

export const ClaimableSoonRewardsResponseSchema = z.object({
  claimableSoonEpochs: z.array(ClaimableSoonEpochRewardsSchema),
});

// TypeScript types inferred from schemas
export type ClaimBatch = z.infer<typeof ClaimBatchSchema>;
export type ClaimablesData = z.infer<typeof ClaimablesDataSchema>;
export type ClaimableRewards = z.infer<typeof ClaimableRewardsSchema>;
export type ClaimableSoonEpochRewards = z.infer<typeof ClaimableSoonEpochRewardsSchema>;

export const ClaimableRewardsResponse = z.object({
  claimableEpochs: ClaimableRewardsSchema,
});
export type ClaimableRewardsResponse = z.infer<typeof ClaimableRewardsResponse>;

export const ClaimableSoonRewardsResponse = z.object({
  claimableSoonEpochs: z.array(ClaimableSoonEpochRewardsSchema),
});
export type ClaimableSoonRewardsResponse = z.infer<typeof ClaimableSoonRewardsResponse>;

// Merkl-distributed rewards. Amounts are token base units (amount × 10^decimals), as strings.
export const MerklCampaignRewardSchema = z.object({
  campaignId: z.string(),
  campaignTitle: z.string(),
  rewardTokenSymbol: z.string(),
  rewardTokenDecimals: z.number().int().nonnegative(),
  /** In Merkl's on-chain root and not claimed yet. */
  claimable: z.string(),
  /** Published by Cede, not yet in Merkl's on-chain root. */
  claimableSoon: z.string(),
  claimed: z.string(),
});

export const MerklClaimTransactionSchema = z.object({
  chainId: z.number(),
  to: z.string(),
  data: z.string(),
  /** What the claim pays out: every Merkl reward the wallet holds in this token on this chain, not only Cede's. */
  walletClaimableAmount: z.string(),
  /** The cumulative amount the claim proves: unchanged across Merkl roots until new rewards arrive. */
  cumulativeAmount: z.string(),
});

export const MerklClaimableRewardsResponseSchema = z.object({
  campaigns: z.array(MerklCampaignRewardSchema),
  claimTransaction: MerklClaimTransactionSchema.nullable(),
});

export type MerklCampaignReward = z.infer<typeof MerklCampaignRewardSchema>;
export type MerklClaimTransaction = z.infer<typeof MerklClaimTransactionSchema>;
export type MerklClaimableRewardsResponse = z.infer<typeof MerklClaimableRewardsResponseSchema>;
